'use client';

import { ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import type { CheckoutStep } from '@/features/totems/presentation/components/web-totem/checkout/checkout-flow-reducer';
import { CheckoutCashierStep } from '@/features/totems/presentation/components/web-totem/checkout/steps/checkout-cashier-step';
import { CheckoutErrorStep } from '@/features/totems/presentation/components/web-totem/checkout/steps/checkout-error-step';
import { CheckoutMethodStep } from '@/features/totems/presentation/components/web-totem/checkout/steps/checkout-method-step';
import { CheckoutMpRedirectStep } from '@/features/totems/presentation/components/web-totem/checkout/steps/checkout-mp-redirect-step';
import { CheckoutProcessingStep } from '@/features/totems/presentation/components/web-totem/checkout/steps/checkout-processing-step';
import { CheckoutQrPaymentStep } from '@/features/totems/presentation/components/web-totem/checkout/steps/checkout-qr-payment-step';
import { CheckoutWaitingTerminalStep } from '@/features/totems/presentation/components/web-totem/checkout/steps/checkout-waiting-terminal-step';
import {
  CheckoutFlowProvider,
  useCheckoutFlowActions,
  useCheckoutFlowState,
} from '@/features/totems/presentation/providers/web-totem/checkout-flow-provider';
import { useTakeOrderFlowEvents } from '@/features/totems/presentation/providers/web-totem/take-order-flow-provider';
import {
  useTotemCartState,
  useTotemCatalog,
  useTotemOrder,
} from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { cn } from '@/shared/utils/utils';

/**
 * Shell visual del cobro: monta el flujo de pago y renderiza el step actual.
 * No tiene lógica de cobro — todo vive en `CheckoutFlowProvider`.
 */
export function TotemCheckoutDialog() {
  const dispatch = useTakeOrderFlowEvents();

  return (
    <CheckoutFlowProvider onEvent={dispatch}>
      <TotemCheckoutDialogShell />
    </CheckoutFlowProvider>
  );
}

function getStepTitle(step: CheckoutStep, isWebTotem: boolean): string {
  switch (step) {
    case 'select_method':
      return isWebTotem ? 'Confirmar y pagar' : 'Cobrar pedido';
    case 'creating_order':
      return 'Procesando...';
    case 'awaiting_terminal':
      return 'Pago Tarjeta';
    case 'awaiting_customer_payment':
      return 'Pago QR';
    case 'redirecting_to_mp':
      return 'Mercado Pago';
    case 'closing_order':
      return 'Confirmando cobro';
    case 'cashier_confirmation':
      return 'Pago exitoso';
    case 'payment_error':
      return 'Cobro no completado';
  }
}

function TotemCheckoutDialogShell() {
  const {
    step,
    qrMode,
    qrData,
    cashNumber,
    errorMessage,
    canRetryClose,
    availableMethods,
    amountTendered,
    isPending,
    isWebTotem,
    isDashboard,
  } = useCheckoutFlowState();
  const { selectMethod, goBack, closeDialog, retryClose, setAmountTendered } = useCheckoutFlowActions();
  const { totem } = useTotemCatalog();
  const { checkoutOpen } = useTotemOrder();
  const { cart } = useTotemCartState();

  // Durante el cierre o la redirección a Mercado Pago el cobro ya está en vuelo: no se puede volver.
  const isBackDisabled = step === 'closing_order' || step === 'redirecting_to_mp';

  return (
    <Dialog
      open={checkoutOpen}
      onOpenChange={open => {
        if (!open) void closeDialog();
      }}
    >
      <DialogContent
        className="flex max-h-[95vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden border-0 bg-background/95 p-0 backdrop-blur-md sm:max-w-[95vw] md:max-w-6xl lg:max-w-7xl"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Cobrar pedido</DialogTitle>
        <DialogDescription className="sr-only">Seleccione un método de pago y confirme el pedido.</DialogDescription>

        <div className="flex h-full min-h-[60vh] flex-1 flex-col">
          <div className="flex shrink-0 items-center gap-4 border-b bg-background px-6 py-4">
            <button
              type="button"
              onClick={goBack}
              disabled={isBackDisabled}
              className={cn(
                'flex size-10 items-center justify-center rounded-full bg-muted/50 transition-colors hover:bg-muted',
                isBackDisabled && 'cursor-not-allowed opacity-40 hover:bg-muted/50'
              )}
            >
              <ArrowLeft className="size-5" />
            </button>
            <h2 className="text-xl font-bold">{getStepTitle(step, isWebTotem)}</h2>
          </div>

          {step === 'select_method' && (
            <CheckoutMethodStep
              methods={availableMethods}
              cartTotal={cart.total}
              amountTendered={amountTendered}
              onAmountTenderedChange={setAmountTendered}
              onSelect={selectMethod}
              isPending={isPending}
              isWebTotem={isWebTotem}
              isDashboard={isDashboard}
            />
          )}

          {step === 'creating_order' && <CheckoutProcessingStep label="Estamos creando tu pedido..." />}

          {step === 'closing_order' && <CheckoutProcessingStep label="Confirmando el cobro y asignando el número..." />}

          {step === 'awaiting_terminal' && <CheckoutWaitingTerminalStep />}

          {step === 'awaiting_customer_payment' && (
            <CheckoutQrPaymentStep qrMode={qrMode} qrData={qrData} totemId={totem?.id ?? null} total={cart.total} />
          )}

          {step === 'redirecting_to_mp' && <CheckoutMpRedirectStep />}

          {step === 'cashier_confirmation' && <CheckoutCashierStep cashNumber={cashNumber} />}

          {step === 'payment_error' && (
            <CheckoutErrorStep
              message={errorMessage}
              onRetry={canRetryClose ? retryClose : undefined}
              onBack={goBack}
              onClose={() => void closeDialog()}
              isPending={isPending}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
